import { useEffect, useState } from "react";
import api from "../lib/api";
import MediaCard from "../components/MediaCard";
import { useFeedCache, readFeedCache, restoreScroll } from "../hooks/useFeedCache";
import { readPersisted, writePersisted } from "../hooks/persistentCache";
import { useAuth } from "../context/AuthContext";
import { Loader2, Sparkles } from "lucide-react";

export default function ForYou() {
  const { user } = useAuth();
  const pkey = `foryou:${user?.id || "anon"}`;
  const cached = readFeedCache("foryou");
  const stored = cached?.data ? null : readPersisted(pkey);
  const [data, setData] = useState(cached?.data || stored || null);
  const [loading, setLoading] = useState(!cached?.data && !stored);
  const [refreshing, setRefreshing] = useState(!cached?.data && !!stored);

  useEffect(() => {
    if (cached?.data) { restoreScroll(cached.scrollY); return; }
    api.get("/foryou?limit=40")
      .then(({ data }) => { setData(data); writePersisted(pkey, data); })
      .catch(() => setData((d) => d || { items: [], interest: [] }))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useFeedCache("foryou", data?.items || [], true, { data });

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#00E5FF]" size={30} /></div>;

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-16 pb-16" data-testid="foryou-page">
      <div className="flex items-center gap-3 mb-1">
        <Sparkles className="text-[#00E5FF]" size={28} />
        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter text-white">For You</h1>
        {refreshing && <Loader2 data-testid="foryou-refreshing" className="animate-spin text-[#52525B]" size={16} />}
      </div>
      <p className="text-[#A1A1AA] text-sm mb-4">
        {data.personalized ? "Tuned to your taste by the Semantic Index — diverse, not repetitive." : "Popular right now. Like and save a few things and this becomes yours."}
      </p>
      {data.interest?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-8" data-testid="foryou-interest">
          {data.interest.map((k) => (
            <a key={k} href={`/c/${k}`} className="text-[11px] text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/25 px-2.5 py-1 rounded-full hover:bg-[#00E5FF]/20">{k}</a>
          ))}
        </div>
      )}
      <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-2 sm:gap-3" data-testid="foryou-grid">
        {data.items.map((it, i) => (
          <div key={it.id} className="mb-2 sm:mb-3 break-inside-avoid" title={it.why ? `Why: concept ${it.why.concept}, semantic ${it.why.semantic}${it.why.matched?.length ? " · " + it.why.matched.join(", ") : ""}` : ""}>
            <MediaCard item={it} index={i} />
          </div>
        ))}
      </div>
    </div>
  );
}
